import * as React from 'react';
import type { MarkPluginID } from '../plugins';
import type { RenderLeaf } from '../types';
import { TEXT_SOURCE_STYLING_PLUGIN_ID } from '../plugins/textSourceStyling/types';

export type GetRenderLeafOptions = Readonly<{
  /**
   * By passing true, helper automatically spreads any required attributes down to the component. If this is not used,
   * you must spread the attributes onto the component.
   *
   * @default true
   */
  spreadAttributes?: boolean;
}>;

export const getRenderLeaf = <
  PluginOptions extends Readonly<{
    pluginID: MarkPluginID;
    component: React.ComponentType<any>;
  }>,
>(
  { component: Component, pluginID }: PluginOptions,
  { spreadAttributes = true }: GetRenderLeafOptions = { spreadAttributes: true }
): RenderLeaf => {
  const renderLeaf: RenderLeaf = ({ leaf, attributes, children, ...rest }) => {
    const bagToSpread = spreadAttributes ? attributes : { attributes };

    const leafRecord = leaf as unknown as Record<string, unknown>;
    const hasMark =
      leaf.text != null &&
      (pluginID === TEXT_SOURCE_STYLING_PLUGIN_ID ||
        leafRecord[pluginID as keyof typeof leafRecord] != null);

    if (hasMark) {
      return (
        <Component
          data-editor-leaf={pluginID}
          {...bagToSpread}
          leaf={leaf}
          children={children}
          {...rest}
        />
      );
    }

    return children;
  };

  return renderLeaf;
};
